import React from 'react';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import { ModalButtonType, modalService } from '@components/Modal';
import { PromiseCompletion } from '@classes/PromiseCompletion';
import { BlockLoader } from '@components/BlockLoader';
import { ToggleFormButton } from '@components/ToggleFormButton';
import { ImgChevronDown } from '@components/Images/ImgChevronDown';
import { ImgMagic } from '@components/Images/ImgMagic';
import apiService from '@services/apiService';
import skillService from '@services/skillService';
import SkillEditDialog, { type SkillDto } from '@components/SkillEditDialog';
import '@components/SettingsDialog/skills.css';
import '@components/SettingsDialog/plugins-tab.css';
import './chat-skills.css';

type Props = {
  enabledSkills: string[];
  onChange: (enabledSkills: string[]) => void;
  loader: PromiseCompletion;
};

@observer
export default class ChatSkillsTab extends React.Component<Props> {
  @observable private _skills: SkillDto[] = [];
  @observable private _error: string | null = null;
  @observable private _expandedId: string | null = null;
  private _innerLoader: PromiseCompletion = new PromiseCompletion();

  constructor(props: Props) {
    super(props);
    makeObservable(this);
  }

  componentDidMount() {
    void this._load();
  }

  @action.bound
  private async _load() {
    this._error = null;
    try {
      const skills = await apiService.get<SkillDto[]>('/api/skills', { loader: this._innerLoader });
      this._skills = skills;
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Failed to load skills';
    }
  }

  @action.bound
  private async _handleAdd() {
    const result = await modalService.show<{ skill: SkillDto | null }, SkillDto | null>(SkillEditDialog, { skill: null });
    if (result.button === ModalButtonType.Save && result.result) {
      skillService.invalidate();
      void skillService.loadSkills();
      await this._load();
    }
  }

  @action.bound
  private async _handleEdit(skill: SkillDto, e: React.MouseEvent) {
    e.stopPropagation();
    const result = await modalService.show<{ skill: SkillDto | null }, SkillDto | null>(SkillEditDialog, { skill });
    if (result.button === ModalButtonType.Save && result.result) {
      skillService.invalidate();
      void skillService.loadSkills();
      await this._load();
    }
  }

  @action.bound
  private async _handleDelete(skill: SkillDto, e: React.MouseEvent) {
    e.stopPropagation();
    const confirmed = await modalService.showConfirmation(
      `Delete skill "${skill.name}"? This cannot be undone.`,
      'Confirm Delete',
      { color: 'danger', buttonTitle: 'Delete' }
    );
    if (!confirmed) return;
    try {
      await apiService.delete(`/api/skills/${skill.id}`, { loader: this._innerLoader });
      skillService.invalidate();
      void skillService.loadSkills();
      // Remove from enabledSkills if it was enabled
      if (this.props.enabledSkills.includes(skill.id)) {
        this.props.onChange(this.props.enabledSkills.filter(id => id !== skill.id));
      }
      await this._load();
    } catch (err) {
      this._error = err instanceof Error ? err.message : 'Failed to delete skill';
    }
  }

  @action
  private _toggleExpand = (id: string) => {
    this._expandedId = this._expandedId === id ? null : id;
  };

  @action
  private _toggleEnabled = (skillId: string, checked: boolean) => {
    const current = this.props.enabledSkills;
    if (checked) {
      this.props.onChange([...current, skillId]);
    } else {
      this.props.onChange(current.filter(id => id !== skillId));
    }
  };

  render() {
    if (this._innerLoader.isPending || this.props.loader.isPending) {
      return <BlockLoader />;
    }

    return (
      <div className="skills-container">
        <div className="skills-header">
          <h3 className="skills-title">Skills</h3>
          <span className="skills-badge">{this._skills.length}</span>
          <button className="add-skill-button" onClick={this._handleAdd}>
            <span className="add-icon">+</span> Add Skill
          </button>
        </div>

        {this._error && (
          <div className="skill-edit-error">{this._error}</div>
        )}

        {this._skills.length === 0 ? (
          <div className="skills-empty">
            No skills yet. Create a skill to delegate tasks to a named sub-agent.
          </div>
        ) : (
          <div className="skills-list">
            {this._skills.map(skill => {
              const expanded = this._expandedId === skill.id;
              const enabled = this.props.enabledSkills.includes(skill.id);
              const allTools = skill.tools.length === 0;
              return (
                <div key={skill.id} className={`plugin-card${enabled ? ' plugin-card--active' : ''}${expanded ? ' plugin-card--expanded' : ''}`}>
                  <div className="plugin-card__header" onClick={() => this._toggleExpand(skill.id)}>
                    <span className="plugin-card__icon"><ImgMagic /></span>
                    <div className="plugin-card__info">
                      <div className="plugin-card__title">{skill.name}</div>
                      {skill.description && (
                        <div className="plugin-card__subtitle">{skill.description}</div>
                      )}
                    </div>
                    <div className="plugin-card__controls">
                      <ToggleFormButton
                        checked={enabled}
                        onChange={(checked) => this._toggleEnabled(skill.id, checked)}
                      />
                      <ImgChevronDown />
                    </div>
                  </div>
                  <div className="plugin-card__body-wrap">
                    <div className="plugin-card__body">
                      {skill.systemPrompt && (
                        <div className="plugin-section">
                          <p className="plugin-section__title">System Prompt</p>
                          <p className="plugin-hint">{skill.systemPrompt}</p>
                        </div>
                      )}
                      <div className="plugin-section">
                        <p className="plugin-section__title">Tools</p>
                        <div className="skill-tools-info">
                          <ToggleFormButton
                            label="All tools"
                            checked={allTools}
                            disabled
                            onChange={() => {}}
                          />
                          {!allTools && (
                            <div className="skill-tools-chips">
                              {skill.tools.map(t => (
                                <span key={t} className="skill-tool-chip">{t}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="plugin-footer">
                        <button
                          className="plugin-btn plugin-btn--secondary"
                          onClick={(e) => this._handleEdit(skill, e)}
                        >
                          Edit
                        </button>
                        <button
                          className="plugin-btn plugin-btn--danger"
                          onClick={(e) => this._handleDelete(skill, e)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }
}
